// Module 6 — Tracking Transactions
// ./index.ts owns this module's key and display name; this file owns its
// action items.

// RDS
import Heading from '@ramsey-design-system/heading';
import { VideoOutlinedIcon } from '@ramsey-design-system/icons';
import Text from '@ramsey-design-system/text';

// Components
import DiscussionQuestions, {
  DiscussionQuestion
} from '../DiscussionQuestions';
import HLSVideoPlayer from '~fpu/shared/components/HLSVideoPlayer';

// Types
import { WorkshopActionItemDefinition } from '../../types';

function ImportanceOfTrackingClip() {
  return (
    <HLSVideoPlayer
      src="https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/tracking-transactions.mp4"
      thumbnail="https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/tracking-transactions.jpg"
    />
  );
}

function WhatShouldITrack() {
  return (
    <div className="flex flex-col gap-4">
      <Text className="text-4 leading-lg">
        Every dollar you earn and spend deserves to be accounted for.
      </Text>

      <div>
        <Heading level="3" size="small" className="mb-1">
          Track Income
        </Heading>
        <ul className="text-4 leading-lg list-disc pl-5">
          <li>Log every paycheck when it hits your account</li>
          <li>Track side hustle income and anything you sell</li>
          <li>
            If you earn more than you planned, add the extra to a savings goal
            or cover budget gaps
          </li>
          <li>
            Even with a steady paycheck, tracking builds the habit and helps
            catch errors
          </li>
        </ul>
      </div>

      <div>
        <Heading level="3" size="small" className="mb-1">
          Track Expenses
        </Heading>
        <ul className="text-4 leading-lg list-disc pl-5">
          <li>Log every single transaction, all month long</li>
          <li>Subtract each purchase from its budget category as it happens</li>
          <li>
            It doesn&apos;t matter how you pay — cash, card, app, or envelope —
            if you spent it, track it
          </li>
          <li>
            Watching your category balances is how you avoid overspending before
            the month ends
          </li>
        </ul>
      </div>
    </div>
  );
}

function TrackingMethods() {
  return (
    <div className="flex flex-col gap-2">
      <Text className="text-4 leading-lg">
        The best tracking method is the one that fits your life.
      </Text>

      <ul className="text-4 leading-lg ml-2 flex flex-col gap-2">
        <li>
          <span className="font-semibold">Pencil and Paper</span> — Simple, no
          tech needed. Best if writing things down helps you stay focused.
          Downside: easy to lose receipts or make math errors.
        </li>
        <li>
          <span className="font-semibold">Cash Envelopes</span> — Label
          envelopes by category and fill them with your budgeted cash. When the
          envelope is empty, you stop spending. No app required, but
          inconvenient for online purchases.
        </li>
        <li>
          <span className="font-semibold">Spreadsheet</span> — Customizable and
          does the math for you. Downside: you have to be at your computer to
          log transactions, which makes it easy to fall behind.
        </li>
        <li>
          <span className="font-semibold">Budgeting App</span> — The most
          convenient option. Track from your phone anywhere, math is automatic,
          and easy to share with a spouse. EveryDollar is Ramsey&apos;s
          recommendation.
        </li>
      </ul>

      <Text className="text-4 leading-lg">
        It does not matter how you track; what matters is that you do it
        consistently. Pick one method and stick with it.
      </Text>
    </div>
  );
}

function EveryDollarBankConnect() {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
      <img
        src="https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/everydollar-screen.png"
        alt="EveryDollar mobile app showing bank-connected transactions"
        className="h-[175px] shrink-0 self-center"
      />

      <div className="flex flex-col gap-2">
        <Heading level="3" size="small">
          Track Transactions with EveryDollar
        </Heading>
        <ul className="text-4 leading-lg list-disc pl-5">
          <li>
            Bank Connect is included in your Financial Peace All Access
            membership
          </li>
          <li>
            Stream transactions directly from your bank to your budget
            automatically
          </li>
          <li>
            Check balances on the go and see upcoming expenses at a glance
          </li>
          <li>
            No manual entry, no missed purchases, no catching up at the end of
            the month
          </li>
        </ul>
      </div>
    </div>
  );
}

const DISCUSSION_QUESTIONS: DiscussionQuestion[] = [
  {
    icon: 'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/discussion-icon-2.png',
    text: 'Have you ever reached the end of the month and had no idea where your money went?'
  },
  {
    icon: 'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/discussion-icon-1.png',
    text: 'How are you currently tracking your spending — and is it actually working for you?'
  },

  {
    icon: 'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/discussion-icon-3.png',
    text: 'What is the biggest thing that causes you to fall off track with your budget mid-month?'
  }
];

function Discussion() {
  return <DiscussionQuestions questions={DISCUSSION_QUESTIONS} />;
}

const actionItems: WorkshopActionItemDefinition[] = [
  {
    title: 'Importance of Tracking',
    Content: ImportanceOfTrackingClip,
    icon: VideoOutlinedIcon,
    duration: '1:40'
  },
  { title: 'What Should I Track', Content: WhatShouldITrack },
  { title: 'Tracking Methods', Content: TrackingMethods },
  { title: 'EveryDollar Bank Connect', Content: EveryDollarBankConnect },
  { title: 'Discussion', Content: Discussion }
];

export default actionItems;
